import React from 'react'
import Box from '@mui/material/Box'
import moment, { Moment } from 'moment'
import { DateTimeFieldControl } from '../components/common/formFields'
import type { FieldSurface } from '../components/common/formFields/formFieldTokens'

type DateValue = Moment | string | null | undefined

export interface DatePickerProps {
  value?: DateValue
  defaultValue?: DateValue
  onChange?: (date: Moment | null, dateString: string) => void
  format?: string
  showTime?: boolean | object
  disabled?: boolean
  style?: React.CSSProperties
  className?: string
  placeholder?: string
  allowClear?: boolean
  picker?: 'date' | 'week' | 'month' | 'year'
  disabledDate?: (current: Moment) => boolean
  /** Surface formFields (mặc định form — cùng chiều cao TextInput trên form). */
  surface?: FieldSurface
  fullWidth?: boolean
  [key: string]: unknown
}

function toControlValue(val: DateValue, withTime: boolean, fmt: string): string {
  if (val == null || val === '') return ''
  const m = moment.isMoment(val) ? val : moment(val, [fmt, 'YYYY-MM-DD', 'YYYYMMDD', 'DD/MM/YYYY', moment.ISO_8601], true)
  if (!m.isValid()) {
    const loose = moment.isMoment(val) ? val : moment(val)
    if (!loose.isValid()) return ''
    return withTime ? loose.format('YYYY-MM-DDTHH:mm') : loose.format('YYYY-MM-DD')
  }
  return withTime ? m.format('YYYY-MM-DDTHH:mm') : m.format('YYYY-MM-DD')
}

function emitFromControl(
  raw: string,
  withTime: boolean,
  format: string,
): { m: Moment | null; str: string } {
  if (!raw) return { m: null, str: '' }
  const m = moment(raw, withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD', true)
  if (!m.isValid()) return { m: null, str: '' }
  const outFmt = format || (withTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD')
  return { m, str: m.format(outFmt) }
}

/**
 * Base chọn ngày / ngày giờ (cùng `DateTimeFieldControl` bán hàng).
 * Giữ API cũ (moment) để filter/form cũ không phải sửa hàng loạt.
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  format = 'YYYY-MM-DD',
  showTime,
  disabled,
  style,
  className,
  surface = 'form',
  fullWidth = true,
}) => {
  const withTime = !!showTime
  const controlValue = toControlValue(value, withTime, format)

  return (
    <Box className={className} sx={{ width: fullWidth ? '100%' : 'auto', minWidth: 0, ...style }}>
      <DateTimeFieldControl
        value={controlValue}
        onChange={(next) => {
          const { m, str } = emitFromControl(String(next ?? ''), withTime, format)
          onChange?.(m, str)
        }}
        disabled={disabled}
        fullWidth={fullWidth}
        surface={surface}
        dateOnly={!withTime}
        showIcon
      />
    </Box>
  )
}

export interface RangePickerProps {
  value?: [DateValue, DateValue] | null
  onChange?: (dates: [Moment | null, Moment | null] | null, dateStrings: [string, string]) => void
  format?: string
  disabled?: boolean
  style?: React.CSSProperties
  className?: string
  allowClear?: boolean
  surface?: FieldSurface
  [key: string]: unknown
}

export const RangePicker: React.FC<RangePickerProps> = ({
  value,
  onChange,
  format = 'YYYY-MM-DD',
  disabled,
  style,
  className,
  surface = 'form',
}) => {
  const start = value?.[0]
  const end = value?.[1]

  const emit = (which: 0 | 1, raw: string) => {
    const { m: m0 } = emitFromControl(
      which === 0 ? raw : toControlValue(start, false, format),
      false,
      format,
    )
    const { m: m1 } = emitFromControl(
      which === 1 ? raw : toControlValue(end, false, format),
      false,
      format,
    )
    const dates: [Moment | null, Moment | null] = [m0, m1]
    onChange?.(dates, [dates[0]?.format(format) ?? '', dates[1]?.format(format) ?? ''])
  }

  return (
    <Box
      className={className}
      sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', minWidth: 0, ...style }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <DateTimeFieldControl
          value={toControlValue(start, false, format)}
          onChange={(next) => emit(0, String(next ?? ''))}
          disabled={disabled}
          fullWidth
          surface={surface}
          dateOnly
          showIcon
        />
      </Box>
      <span>~</span>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <DateTimeFieldControl
          value={toControlValue(end, false, format)}
          onChange={(next) => emit(1, String(next ?? ''))}
          disabled={disabled}
          fullWidth
          surface={surface}
          dateOnly
          showIcon
        />
      </Box>
    </Box>
  )
}

export type DatePickerComponent = React.FC<DatePickerProps> & {
  RangePicker: typeof RangePicker
}

const DatePickerRoot = DatePicker as DatePickerComponent
DatePickerRoot.RangePicker = RangePicker

export { DatePickerRoot as DatePickerExport }
export default DatePickerRoot
